import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Dayjs } from "dayjs";
import { apiGet, apiPost, apiPut, type ApiError } from "../api";
import type { ID } from "../id";

/** One row of the mail outbox (internal/handlers/api/mail_outbox.go). */
export interface MailOutboxRow {
  id: ID;
  public_id: string;
  kind: string;
  entity_type: string;
  entity_ref: string;
  address: string;
  name: string;
  status: MailOutboxStatus;
  attempts: number;
  last_error: string;
  /** The last attempt failed on letterman's setup. */
  config_error: boolean;
  /** A worker holds the row right now: it can be neither hurried nor stopped. */
  in_flight: boolean;
  created_at: Dayjs;
  not_before: Dayjs;
  expires_at: Dayjs;
  sent_at: Dayjs | null;
  /** The message in letterman's admin, once letterman has it. */
  admin_url: string;
}

export type MailOutboxStatus =
  | "pending"
  | "sent"
  | "cancelled"
  | "expired"
  | "failed"
  | "unreachable"
  /** Owed while its kind was switched off; never sent. */
  | "disabled";

export interface MailOutboxListResponse {
  items: MailOutboxRow[];
  total: number;
  page: number;
  per_page: number;
}

export interface MailOutboxParams {
  status?: string;
  kind?: string;
  q?: string;
  page?: number;
  per_page?: number;
}

export function useMailOutbox(params: MailOutboxParams) {
  const search = new URLSearchParams();
  if (params.status) search.set("status", params.status);
  if (params.kind) search.set("kind", params.kind);
  if (params.q) search.set("q", params.q);
  if (params.page != null) search.set("page", String(params.page));
  if (params.per_page != null) search.set("per_page", String(params.per_page));
  const qs = search.toString();
  return useQuery<MailOutboxListResponse>({
    queryKey: ["mail-outbox", params],
    queryFn: () => apiGet<MailOutboxListResponse>(`/mail-outbox${qs ? `?${qs}` : ""}`),
    placeholderData: (prev) => prev,
    // A waiting row leaves within a minute; a 30 s cache would show it as
    // waiting after it has gone.
    staleTime: 0,
  });
}

function useOutboxAction(action: "send-now" | "cancel") {
  const qc = useQueryClient();
  return useMutation<void, ApiError, ID>({
    mutationFn: (id) => apiPost<void>(`/mail-outbox/${id}/${action}`),
    // The confirmation dialog shows the refusal beside its button.
    meta: { suppressErrorToast: true },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mail-outbox"] });
    },
  });
}

/** Lets a waiting mail out on the worker's next pass. */
export function useSendMailNow() {
  return useOutboxAction("send-now");
}

/** Stops a waiting mail. */
export function useCancelMail() {
  return useOutboxAction("cancel");
}

/**
 * One toggle on the settings tab (`mail.Sections`). A pair of kinds shares
 * one switch: the server refuses half of it.
 */
export interface MailSwitch {
  kinds: string[];
  /** Always on: the mail carries a set-password link. */
  locked: boolean;
  enabled: boolean;
  timing: MailTiming;
}

/** What a reminder counts its days back from (`mail.Deadline`). */
export type MailDeadline =
  | "registration_opens"
  | "registration_closes"
  | "entry_deadline"
  | "roster_deadline"
  | "music_deadline"
  | "competition_date";

/**
 * When a switch's mail leaves (`mail.Timing`), from the constants the server
 * sends by. An event mail has no `deadline` and waits `delay_minutes`; a
 * reminder counts `days_before` back from its `deadline`.
 */
export interface MailTiming {
  delay_minutes: number;
  deadline?: MailDeadline;
  days_before?: number[];
  days_after?: number;
  published_for_days?: number;
}

/** The scheduler's rules for every reminder (`mail.ReminderRules`). */
export interface MailReminderRules {
  from_hour: number;
  until_hour: number;
  grace_days: number;
}

export interface MailSettingsSection {
  key: string;
  switches: MailSwitch[];
}

export interface MailSettings {
  sections: MailSettingsSection[];
  reminders: MailReminderRules;
}

export function useMailSettings() {
  return useQuery<MailSettings>({
    queryKey: ["mail-settings"],
    queryFn: () => apiGet<MailSettings>("/mail-settings"),
  });
}

/** Replaces the set of switched-off kinds; answers how many waiting mails it stopped. */
export function useSaveMailSettings() {
  const qc = useQueryClient();
  return useMutation<{ rows_disabled: number }, ApiError, string[]>({
    mutationFn: (disabled) => apiPut<{ rows_disabled: number }>("/mail-settings", { disabled }),
    // A switch-off shows the refusal in its confirmation, a switch-on on the page.
    meta: { suppressErrorToast: true },
    // Returned, so the mutation stays pending until the switches are
    // refetched: the next toggle builds its list from them, and a stale copy
    // would switch the previous change back.
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ["mail-settings"] }),
        qc.invalidateQueries({ queryKey: ["mail-outbox"] }),
      ]),
  });
}
